import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Target, Eye, Heart, Lightbulb, Users, Shield, Zap, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const values = [
  {
    icon: Award,
    title: 'Excellence',
    description: 'Nous visons la perfection dans chaque projet, avec un souci du détail qui fait la différence.',
  },
  {
    icon: Shield,
    title: 'Transparence',
    description: 'Communication claire et honnête à chaque étape de votre projet.',
  },
  {
    icon: Users,
    title: 'Proximité',
    description: 'Un accompagnement personnalisé avec un interlocuteur dédié à votre écoute.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Nous adoptons les dernières technologies pour des solutions modernes et performantes.',
  },
  {
    icon: Heart,
    title: 'Engagement',
    description: 'Votre succès est notre priorité. Nous nous investissons pleinement dans chaque projet.',
  },
  {
    icon: Zap,
    title: 'Performance',
    description: 'Des solutions optimisées pour des résultats concrets et mesurables.',
  },
];

const team = [
  {
    name: 'Équipe Développement',
    role: 'Experts techniques',
    description: 'Développeurs passionnés maîtrisant les technologies modernes du web.',
  },
  {
    name: 'Équipe Design',
    role: 'Créatifs UX/UI',
    description: 'Designers créatifs focalisés sur l\'expérience utilisateur.',
  },
  {
    name: 'Équipe Marketing',
    role: 'Stratèges digitaux',
    description: 'Experts en SEO, SEA et stratégies de croissance digitale.',
  },
];

export const About = () => {
  const pageTitle = "À propos | Weblax's Studio - Agence web, création de site internet & SEO";
  const pageDescription =
    "Découvrez Weblax's Studio, agence web spécialisée en création de site internet, design UX/UI, développement web et référencement SEO. Une équipe passionnée au service de votre croissance digitale.";
  const pageUrl = 'https://www.weblax.fr/about';
  const pageImage = 'https://www.weblax.fr/og-image.jpg';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: "À propos | Weblax's Studio",
    url: pageUrl,
    description: pageDescription,
    mainEntity: {
      '@type': 'Organization',
      name: "Weblax's Studio",
      url: 'https://www.weblax.fr',
      logo: 'https://www.weblax.fr/logo.png',
      description:
        "Agence digitale spécialisée dans la création de sites internet, le développement web, le design UX/UI et le référencement SEO.",
      sameAs: [],
    },
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="à propos agence web, agence digitale, création site internet, développement web, référencement SEO, design UX UI, Weblax Studio"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax's Studio" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div data-testid="about-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28">
              <div className="max-w-3xl">
                <p className="weblax-caption text-primary mb-4">À propos</p>
                <h1 className="weblax-heading-display mb-6">
                  Une équipe passionnée à votre service
                </h1>
                <p className="weblax-body-lg">
                  Weblax est une agence digitale créative qui accompagne les entreprises
                  dans leur transformation numérique. Notre mission : créer des expériences
                  digitales qui inspirent et convertissent.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="weblax-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="weblax-heading-2 mb-4">Notre Mission</h2>
                  <p className="weblax-body-lg">
                    Aider les entreprises, marques et indépendants à développer leur présence
                    digitale grâce à des sites internet, applications et stratégies marketing
                    performantes. Nous transformons vos idées en succès numériques.
                  </p>
                </CardContent>
              </Card>
              <Card className="weblax-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                    <Eye className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="weblax-heading-2 mb-4">Notre Vision</h2>
                  <p className="weblax-body-lg">
                    Rendre le web plus accessible, esthétique et performant pour les entreprises
                    de toutes tailles. Nous croyons que chaque entreprise mérite une présence
                    digitale de qualité qui reflète son excellence.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Nos valeurs</p>
              <h2 className="weblax-heading-1 mb-4">
                Ce qui nous anime
              </h2>
              <p className="weblax-body-lg">
                Ces valeurs guident chacune de nos actions et définissent notre façon de travailler.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <Card key={index} className="weblax-card group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                    <p className="text-muted-foreground text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Notre équipe</p>
              <h2 className="weblax-heading-1 mb-4">
                Des experts à votre service
              </h2>
              <p className="weblax-body-lg">
                Une équipe pluridisciplinaire qui combine expertise technique,
                créativité et vision stratégique.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {team.map((member, index) => (
                <Card key={index} className="weblax-card text-center">
                  <CardContent className="p-8">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-10 h-10 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-1">{member.name}</h3>
                    <p className="text-primary text-sm mb-3">{member.role}</p>
                    <p className="text-muted-foreground text-sm">{member.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="weblax-caption text-primary mb-3">Pourquoi nous choisir</p>
                <h2 className="weblax-heading-1 mb-6">
                  Performance. Créativité. Simplicité.
                </h2>
                <p className="weblax-body-lg mb-6">
                  Notre slogan n'est pas qu'une promesse, c'est notre engagement quotidien.
                  Chaque projet que nous réalisons est conçu pour être performant,
                  créatif et simple à utiliser.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 bg-primary rounded-full" />
                    </div>
                    <span className="text-muted-foreground">
                      Plus de 150 projets livrés avec succès
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 bg-primary rounded-full" />
                    </div>
                    <span className="text-muted-foreground">
                      98% de clients satisfaits
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 bg-primary rounded-full" />
                    </div>
                    <span className="text-muted-foreground">
                      Accompagnement de A à Z
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 bg-primary rounded-full" />
                    </div>
                    <span className="text-muted-foreground">
                      Technologies modernes et évolutives
                    </span>
                  </li>
                </ul>
                <Link to="/contact">
                  <Button className="weblax-btn-primary">
                    Discutons de votre projet
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="text-center p-8">
                    <p className="text-6xl font-bold text-primary mb-4">5+</p>
                    <p className="text-xl font-semibold mb-2">Années d'expérience</p>
                    <p className="text-muted-foreground">
                      Au service de votre réussite digitale
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Prêt à collaborer ?
                </h2>
                <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                  Contactez-nous pour discuter de votre projet et découvrir comment nous pouvons vous aider.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium">
                      Demander un devis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-3 rounded-full">
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;